import { useCallback, createContext, useState } from "react";
import {
  getFromLocalStorage, setToLocalStorage, removeFromLocalStorage } from "../../lib/localStorage";

const PluginsContext = createContext();

export function PluginsContextProvider({ children }) {
    const [activePluginsByAccount, setActivePluginsByAccount] = useState(() => {
        try {
            return getFromLocalStorage('activePlugins', []);
        } catch (error) {
            console.error("Failed to parse active plugins from localStorage", error);
            return [];
        }
    });

    // Postojana: `App.jsx` je drži u `useEffect`-u nad odgovorom o pluginima.
    // Radi nad `prev` i piše u `localStorage`, pa zavisnosti nema. Bez ovoga
    // bi effect koji je zove pravio petlju: nov niz -> novo stanje ->
    // iscrtavanje -> nova funkcija -> effect.
    const saveActiveToContext = useCallback((newActive, accountName) => {
        setActivePluginsByAccount((prevPlugins) => {
            const updatedPlugins = [...prevPlugins];
            const accountIndex = updatedPlugins.findIndex(el => el.accountName === accountName);
            const newEntry = { active: newActive, accountName };

            if (accountIndex === -1) {
                updatedPlugins.push(newEntry);
            } else {
                updatedPlugins[accountIndex] = newEntry;
            }

            setToLocalStorage('activePlugins', updatedPlugins);
            return updatedPlugins;
        });
    }, []);

    const updatePluginsContext = ({ status, plugin, guiname }) => {
        setActivePluginsByAccount((prevPlugins) => {
            const updatedPlugins = prevPlugins.map(account => {
                if (account.accountName === guiname) {
                    const updatedActive = status
                        ? Array.from(new Set([...account.active, Number(plugin)]))
                        : account.active.filter(item => item !== Number(plugin));
                    return { ...account, active: updatedActive };
                }
                return account;
            });

            // `map` ne dodaje stavke: dok nalog nije u spisku, izmena nije
            // imala na šta da se primeni i tiho je propadala. Spisak se puni
            // tek kad `App.jsx` dobije odgovor sa lanca, pa je dovoljno da
            // korisnik uključi plugin pre toga da se upis izgubi — a na ekranu
            // bi izgledalo kao da je prošao. Zato se nalog ovde zavodi.
            if (!updatedPlugins.some(a => a.accountName === guiname)) {
                updatedPlugins.push({ accountName: guiname, active: status ? [Number(plugin)] : [] });
            }

            setToLocalStorage('activePlugins', updatedPlugins);
            return updatedPlugins;
        });
    };

    const clearPluginsContext = () => {
        setActivePluginsByAccount([]);
        removeFromLocalStorage('activePlugins');
    };

    // Postojana dok se spisak ne promeni: ekrani je drže u `useEffect`-ima
    // koji odlučuju o koraku, pa bi se inače okidali pri svakom iscrtavanju.
    const getActiveByAccountName = useCallback((accountName) => {
        try {
            return activePluginsByAccount.find(acc => acc.accountName === accountName) || null;
        } catch (error) {
            console.error("Failed to get active plugins by account name", error);
            return null;
        }
    }, [activePluginsByAccount]);

    const valueToShare = {
        active: activePluginsByAccount,
        saveActiveToContext,
        clearPluginsContext,
        getActiveByAccountName,
        updatePluginsContext,
    };

    return (
        <PluginsContext.Provider value={valueToShare}>
            {children}
        </PluginsContext.Provider>
    );
}

export default PluginsContext;
